import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogIn, AlertCircle } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user.must_change_password) {
      navigate("/change-password", { replace: true });
    } else if (user) {
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
    else if (res.user.must_change_password) navigate("/change-password", { replace: true });
    else navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen bg-bg bg-grid flex" data-testid="login-page">
      {/* Left brand strip */}
      <div className="hidden md:flex md:w-[46%] flex-col justify-between p-12 border-r border-line bg-bg-surface">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-cyan">
            Main St.
          </div>
          <div className="mt-1 text-2xl font-semibold tracking-tight text-ink">
            Sales Console
          </div>
        </div>

        <div className="max-w-md">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-3">
            Operating principle
          </div>
          <p className="text-lg leading-snug text-ink">
            Lead with a <span className="text-cyan">gift</span>. Then let the
            work speak. Reciprocity flips the call.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6 text-xs">
            <div>
              <div className="text-ink-muted font-mono uppercase tracking-wider text-[10px]">
                Feed
              </div>
              <div className="text-ink mt-1 tabular-nums">~132 leads</div>
            </div>
            <div>
              <div className="text-ink-muted font-mono uppercase tracking-wider text-[10px]">
                Pipeline
              </div>
              <div className="text-ink mt-1">8 stages</div>
            </div>
            <div>
              <div className="text-ink-muted font-mono uppercase tracking-wider text-[10px]">
                Outreach
              </div>
              <div className="text-ink mt-1">Warm · Short · SMS</div>
            </div>
          </div>
        </div>

        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          Internal tool — Authorized reps only
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center px-8 md:px-16">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-2">
            Sign in
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink mb-8">
            Welcome back
          </h1>

          <label className="block mb-4">
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
              Email
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              data-testid="login-email-input"
              className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors"
              placeholder="you@company.com"
            />
          </label>

          <label className="block mb-6">
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">
              Password
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="login-password-input"
              className="w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors"
              placeholder="••••••••"
            />
          </label>

          {error && (
            <div
              className="mb-4 flex items-start gap-2 border border-line rounded-sm px-3 py-2 text-xs"
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.06)",
                borderColor: "rgba(239, 68, 68, 0.3)",
                color: "#F87171",
              }}
              data-testid="login-error"
            >
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || loading}
            data-testid="login-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-cyan text-bg font-medium py-2 rounded-sm hover:bg-cyan-hover transition-colors disabled:opacity-60 active:scale-[0.98]"
          >
            <LogIn size={14} />
            {submitting ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-6 text-[11px] text-ink-muted leading-relaxed">
            First-login users will be required to set a new password.
          </div>
        </form>
      </div>
    </div>
  );
}
